import { useBlocker } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type Props = {
  hasPendingDrafts: boolean
  onConfirmDiscard: () => Promise<void> | void
  /**
   * Fired when the page is actually being discarded. Must be synchronous — nothing can be
   * awaited at that point — which is why it is separate from `onConfirmDiscard`.
   */
  onUnloadDiscard?(): void
}

export const LeaveDraftsGuard = ({
  hasPendingDrafts,
  onConfirmDiscard,
  onUnloadDiscard,
}: Props) => {
  const { t } = useTranslation()
  const [discarding, setDiscarding] = useState(false)

  /**
   * `pagehide`, NOT `beforeunload`.
   *
   * `beforeunload` fires before the native prompt is answered, so discarding there would
   * delete the drafts of someone who then chose to stay. `pagehide` only fires once the
   * page is genuinely going away.
   *
   * `persisted` means the page went into the back/forward cache and may be restored intact
   * — its drafts must survive that, or returning would show an empty page.
   */
  useEffect(() => {
    if (!hasPendingDrafts || !onUnloadDiscard) return

    const onPageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return

      onUnloadDiscard()
    }

    window.addEventListener('pagehide', onPageHide)

    return () => window.removeEventListener('pagehide', onPageHide)
  }, [hasPendingDrafts, onUnloadDiscard])

  const blocker = useBlocker({
    shouldBlockFn: () => hasPendingDrafts,
    enableBeforeUnload: hasPendingDrafts,
    withResolver: true,
  })

  const open = blocker.status === 'blocked'

  const handleConfirm = async () => {
    if (blocker.status !== 'blocked') return
    setDiscarding(true)
    try {
      await onConfirmDiscard()
      blocker.proceed()
    } finally {
      setDiscarding(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && blocker.reset?.()}>
      <DialogContent hideCloseButton className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('draftsLeaveGuard.title')}</DialogTitle>
          <DialogDescription>{t('draftsLeaveGuard.body')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => blocker.reset?.()} disabled={discarding}>
            {t('draftsLeaveGuard.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={discarding}>
            {t('draftsLeaveGuard.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
